<template>
  <div id="core-layout">
    <AppHeader ref="appHeader"></AppHeader>
    <el-row type="flex">
      <el-col id="nav-col" style="max-width: 165px !important;" v-if="authStatus && $route.path !== '/login'">
        <NavSide v-if="showNav" ref="navSide"></NavSide>
      </el-col>
      <el-col id="core-col" :span="authStatus && $route.path !== '/login' ? 22 : 24" class="core-wrapper-header">
        <div id="core-wrapper" v-bind:style="{ height: height }" v-loading.body="navigating || transactionsLoading" element-loading-text="Loading...">
          <router-view></router-view>
        </div>
      </el-col>
    </el-row>
    <ReportViewer
      v-model:reportViewerVisible="reportViewerVisible"
      :reportId="reportId"
      @setVisibilityState="setReportViewerVisibilityState">
    </ReportViewer>
    <Report v-if="authStatus"></Report>
    <CustomerVendorForm :type="customerVendorFormType" v-if="renderNewVendorComponent && authStatus" :modelId="modelId" @updateModelIdProp="updateVendorModelId"></CustomerVendorForm>
    <ChangeLossModal />
    <ProscanUploadBatchPanel v-if="authStatus && $route.path.includes('proscan')" />
    <!-- <RemainderModal /> -->
    <AppFooter ref="appFooter"></AppFooter>
  </div>
</template>

<script>
  import { h } from 'vue'
  import { mapGetters } from 'vuex'
  import AppFooter from './Footer'
  import AppHeader from './Header'
  import NavSide from './NavSide'
  import ReportViewer from '@/components/ReportViewer.vue'
  import Report from '@/components/Reports/Report.vue'
  import CustomerVendorForm from '@/components/FormComponents/CustomerVendorForm.vue'
  import { pollReport } from '@/utils/helpers/ReportHelpers/PollReport'
  import { payload as countryTaxCodePayload } from '@/components/RouteComponents/Taxcodes/actionPayload.js'
  import { payload as companyActionPayload } from '@/components/RouteComponents/Companies/actionPayload'
  import { payload as productionActionPayload } from '@/components/RouteComponents/Productions/actionPayload'
  import { payload as bankActionPayload } from '@/components/RouteComponents/Banks/actionPayload'
  import { payload as stateActionPayload } from '@/components/RouteComponents/States/actionPayload'
  import { payload as countryActionPayload } from '@/components/RouteComponents/Countries/actionPayload'
  import { payload as currencyActionPayload } from '@/components/RouteComponents/Currencies/actionPayload'

  import ChangeLossModal from '@/components/ChangeLossModal.vue'
  import ProscanUploadBatchPanel from '@/components/RouteComponents/Proscan/ProscanUploadBatchPanel.vue'
  // import RemainderModal from '../components/RemainderModal.vue'
  import { DataLoader } from '@/utils/helpers/DataLoader/DataLoader'

  export default {
    components: {
      AppHeader,
      NavSide,
      AppFooter,
      ReportViewer,
      Report,
      CustomerVendorForm,
      ChangeLossModal,
      ProscanUploadBatchPanel
      // RemainderModal
    },
    name: 'coreLayout',
    data () {
      return {
        modelId: null,
        height: `${window.innerHeight - 120}px`,
        showNav: false,
        reportNotif: '',
        reportViewerVisible: false,
        reportId: '',
        excludedRoutes: {
          newVendorModal: ['NewAp', 'EditAp', 'NewPo', 'EditPo', 'NewPc', 'EditPc', 'Vendors', 'Customers', 'Proscan']
        },
        renderNewVendorComponent: true,
        spinnerTimer: null
      }
    },
    methods: {
      /**
       * Updates modelId prop passed to customer create/edit modal
       */
      updateVendorModelId (id) {
        this.modelId = id
      },
      openReport (reportId) {
        this.reportNotif.close()
        const report = this.getReportById(reportId)
        this.reportViewerVisible = true
        return this.$store.dispatch('setReportViewStatus', {
          reportType: report.type,
          id: reportId,
          isViewed: true
        })
      },
      /**
       * Governs whether the report viewer is visible
       */
      setReportViewerVisibilityState (visibleState) {
        this.reportViewerVisible = visibleState
      },
      /**
       * Verifies user authentication status and whether settings have been checked. If so, calls the loadData method
       */
      checkAuthStatus () {
        if (this.authStatus && !!Object.keys(this.settings).length) {
          if (!this.dataLoaded && !this.dataLoading) {
            this.loadData()
          }
          this.showNav = true
        } else {
          this.$store.dispatch('setInitDataLoadStatus', false)
          this.showNav = false
          this.$router.push({ name: 'Login' })
        }
      },
      /**
       * Prefetches data for this instance. Instantiates a new DataLoader object, then calls the loadData class method
       * on the newly instantiated DataLoader object.
       */
      loadData () {
        let dataLoader = new DataLoader(this.$store, this.modelActionPayloads, true, 200)
        dataLoader.loadData()
      }
    },
    computed: {
      ...mapGetters({
        authStatus: 'isLoggedIn',
        settings: 'getUiSettings',
        report: 'getReports',
        getReportById: 'getReportById',
        receivingReport: 'getReportFetchStatus',
        renderingReport: 'getReportRenderStatus',
        reportErr: 'getReportError',
        createModalVisible: 'getModalVisibilityState',
        navigating: 'getNavigationStatus',
        getAddressById: 'getAddressById',
        dataLoaded: 'getInitDataLoadedStatus',
        dataLoading: 'getInitDataLoadingStatus',
        transactionsLoading: 'getTransactionsLoadingStatus'
      }),
      customerVendorFormType () {
        const visible = this.$store.getters.getModalVisibilityState
        if (visible.customerVendorForm && typeof visible.customerVendorForm === 'object' && visible.customerVendorForm.visible) {
          return visible.customerVendorForm.type
        }
        return 'vendor'
      },
      arEnabled () {
        if (this.settings) {
          return !!this.settings['AR_ENABLED']
        }
      },
      payrollEnabled () {
        if (this.settings) {
          return !!this.settings['PAYROLL_ENABLED']
        }
      },
      modelActionPayloads () {
        let payloads = [
          companyActionPayload,
          countryTaxCodePayload,
          productionActionPayload,
          bankActionPayload,
          stateActionPayload,
          countryActionPayload,
          currencyActionPayload
        ]
        if (this.arEnabled) {
          const arPayloads = []
          payloads = [...payloads, ...arPayloads]
        }
        return payloads
      }
    },
    watch: {
      receivingReport () {
        if (!this.receivingReport) return
        const { status, reportId } = this.receivingReport
        if (this.receivingReport && status && reportId) {
          this.reportNotif = this.$message({
            message: 'Building report. Please wait...',
            duration: 0
          })
        } else if (!status && !this.reportErr && (reportId && this.report[reportId])) {
          this.reportNotif.close()
          this.reportId = reportId
          this.reportNotif = this.$message({
            message: h('p', null, [
              h('span', null, 'Report ready.  '),
              h('button', { onClick: () => this.openReport(reportId) }, 'Open')
            ]),
            type: 'success',
            showClose: true,
            duration: 0
          })
        } else if (!status && this.reportErr && !this.report[reportId]) {
          this.reportNotif.close()
          this.reportNotif = this.$message({
            message: 'Error building report. Please try again.',
            type: 'error',
            showClose: true,
            duration: 0
          })
        }
      },
      renderingReport () {
        if (this.renderingReport) pollReport(this.$store)
      },
      $route (to) {
        const { newVendorModal } = this.excludedRoutes
        this.renderNewVendorComponent = !newVendorModal.includes(to.name)
      },
      navigating () {
        clearTimeout(this.spinnerTimer)
        this.spinnerTimer = null
        let isNavigating = this.$store.getters.getNavigationStatus
        if (isNavigating) {
          this.spinnerTimer = setTimeout(() => {
            this.$store.dispatch('trackNavigation', false)
          }, 20000)
        }
      },
      settings () {
        this.checkAuthStatus()
      }
    },
    created () {
      const url = new URL(window.location.href)
      if (!url.href.includes('localhost')) {
        const hostnameParts = url.hostname.split('.')
        const baseDomain = hostnameParts.slice(-2).join('.')
        this.$store.commit('SET_INSTANCE', `.${baseDomain}`)
      } else this.$store.commit('SET_INSTANCE', `.sigmacloud.io`)
      if (this.$route.meta.requiresAuth) {
        this.checkAuthStatus()
        const {newVendorModal} = this.excludedRoutes
        this.renderNewVendorComponent = !newVendorModal.includes(this.$route.name)
      }
    }
  }
</script>

<style>
  #report-dialog .el-dialog__header {
    padding: 0;
  }
  .vendor-dialog .el-dialog__header {
    padding: 0;
  }
  .vendor-dialog .el-dialog__body {
    padding: 20px;
  }
  body {
    background: #fff;
  }
  #core-layout {
    width: 100%;
  }
  .core-wrapper-header {
    background-color: #eef1f6;
    overflow-x: hidden;
  }
  #core-wrapper {
    background-color: #eef1f6;
    display: flex;
    flex-direction: row;
    width: 100%;
  }
  #nav-col {
    background: #555c64;
    width: 65px;
  }
  #core-col {
    flex: 1;
    overflow-y: auto;
  }
</style>
